import Icon from '../../../Icon/Icon';
import { jsons2csv } from 'react-csv/lib/core';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../containers/UseAuth';
import { useForecast } from '../../../../contexts/ForecastContext';
import { formatNumber, lastTwoDecimal } from '../../../../utils/functions';
import LinkButton from '../../../Buttons/LinkButton';

const DownloadForecastToCsv = ({
    forecastData,
    tierList,
    selectedScreensLocal,
    campaign,
}) => {
    const { t } = useTranslation();
    const auth = useAuth();
    const campaignId = campaign?.id;
    const brand = campaign?.brand;
    const campaignName = campaign?.name;
    const { forecast: metrics = {} } = useForecast();
    const currencySymbol =
        campaign?.selectedCurrency?.symbol !== undefined
            ? campaign.selectedCurrency.symbol
            : '';
    const user = auth.session.user;
    const nameCSV =
        `Forecast_${user.id}_${user.username}_${brand}_${campaignName}_${campaignId}`.replace(
            / /g,
            ''
        );

    const headers = [
        { label: 'id', key: 'poiId' },
        { label: 'externalId', key: 'ScreenExternalId' },
        { label: 'Screen Name', key: 'screenName' },
        { label: 'Screen Type', key: 'screenType' },
        { label: 'Date', key: 'date' },
        { label: 'Impressions', key: 'impressions' },
        { label: 'Impacts', key: 'totalImpacts' },
        { label: 'Targeted Universe', key: 'targetUniverse' },
        { label: 'Coverage Abs', key: 'totalCoverageAbs' },
        { label: 'Coverage Rel', key: 'totalCoverageRel' },
        { label: 'Costs', key: 'costs' },
        { label: 'CPM', key: 'cpm' },
        { label: 'CPI', key: 'cpi' },
        { label: 'Frequency', key: 'totalFrequency' },
        { label: 'GRP', key: 'grp' },
        { label: 'CPS', key: 'cps' },
        { label: 'SPM', key: 'spm' },
    ];

    const downloadCsv = () => {
        const screensUsedById = new Map(
            metrics.screensUsed?.map((screen) => [screen.id, screen]) ?? []
        );
        const selectedScreensById = new Map(
            selectedScreensLocal.map((screen) => [screen.id, screen])
        );
        const formattedCsv = forecastData.map((row) => {
            let screenTypeRow = row.screenType || '';
            if (!screenTypeRow) {
                const tierId = selectedScreensById.get(row.poiId)?.tierId;
                if (tierId && tierList[tierId]) {
                    screenTypeRow = tierList[tierId];
                }
            }
            const formattedTargetUniverse = row.targetUniverse
                ? formatNumber(row.targetUniverse, 'withcommas')
                : 0;
            const formattedCoverageAbs =
                row.totalCoverageAbs !== undefined &&
                row.totalCoverageAbs !== null
                    ? formatNumber(row.totalCoverageAbs, 'rounded')
                    : 0;
            const formattedCoverageRel = (() => {
                if (
                    row.totalCoverageRel === undefined ||
                    row.totalCoverageRel === null
                ) {
                    return '-';
                }
                const numericValue = Number(row.totalCoverageRel);
                if (Number.isNaN(numericValue)) {
                    return row.totalCoverageRel;
                }
                const percentValue =
                    numericValue <= 1 ? numericValue * 100 : numericValue;
                return `${formatNumber(percentValue, 'cpi')}%`;
            })();
            return {
                ...row,
                screenName:
                    row.screenName || screensUsedById.get(row.poiId)?.name || '',
                screenType: screenTypeRow,
                costs: `${currencySymbol}${formatNumber(row.costs, 'currency')}`,
                targetUniverse: formattedTargetUniverse,
                totalCoverageAbs: formattedCoverageAbs,
                totalCoverageRel: formattedCoverageRel,
                cpm: row.cpm
                    ? `${currencySymbol}${formatNumber(row.cpm, 'currency')}`
                    : `${currencySymbol}0`,
                cpi: row.cpi
                    ? `${currencySymbol}${formatNumber(row.cpi, 'currency')}`
                    : `${currencySymbol}0`,
                totalFrequency: row.totalFrequency
                    ? formatNumber(row.totalFrequency, 'currency')
                    : 0,

                grp: 'grp' in row ? formatNumber(lastTwoDecimal(row.grp)) : 0,
                cps: 'cps' in row ? formatNumber(lastTwoDecimal(row.cps)) : 0,
                spm: 'spm' in row ? formatNumber(lastTwoDecimal(row.spm)) : 0,
            };
        });
        const url = URL.createObjectURL(
            new Blob(['\uFEFF', jsons2csv(formattedCsv, headers)], {
                type: 'text/csv',
            })
        );
        const link = document.createElement('a');
        link.href = url;
        link.download = `${nameCSV}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <LinkButton onClick={downloadCsv}>
            <Icon
                name="file-excel"
                size={12}
                className="text-green-500 cursor-pointer mr-2 align-middle"
            />
            {t(
                'sections.campaigns.new-campaign.wizard-audiences.forecast.csv'
            )}
        </LinkButton>
    );
};

export default DownloadForecastToCsv;
